/**
 * Front elevation (port of a-studio/pack.js `elevationSVG`): carcass parts, the back,
 * the opening chain on the right, shelf-underside datums on the left, floor and width.
 * `elevationModel` gives the geometry for a native Vue renderer; `elevationSvg` a string.
 * SVG user units are millimetres with y flipped (floor at the bottom).
 */
import type { OpeningRow } from '@/domain/schemas';
import type { BoardAssembly } from '@/families/assembly';
import { fmtMm, fmtNum, mm } from '@/domain/units';
import { esc } from './common';

/** What the elevation reads: the BoardAssembly (openings may carry the grid's bay index). */
export type ElevationSource = Pick<BoardAssembly, 'parts' | 'envelope' | 'datums'> & { openings: readonly (BoardAssembly['openings'][number] & Partial<Pick<OpeningRow, 'index' | 'bay'>>)[] };

export interface ElevationRect { id: string; label: string; x: number; y: number; width: number; height: number }
export interface ElevationOpening { index: number; label: string; text: string; x: number; tickX1: number; tickX2: number; yTop: number; yBottom: number; textX: number; textY: number; /** Multi-bay: the size is written inside the opening, with no chain. */ inside?: boolean }
export interface ElevationDatum { shelfNo: number; y: number; x1: number; x2: number; textX: number; text: string }

export interface ElevationModel {
  viewBox: [number, number, number, number];
  W: number;
  H: number;
  fontSize: number;
  /** The (first) back piece seen through the openings, or null with no back. */
  back: ElevationRect | null;
  backs: ElevationRect[];
  parts: ElevationRect[];
  openings: ElevationOpening[];
  datums: ElevationDatum[];
  width: { y: number; tickY1: number; tickY2: number; textX: number; textY: number; text: string; floorX: number; floorY: number };
  ariaLabel: string;
}

export function elevationModel(build: ElevationSource): ElevationModel {
  const W = build.envelope.W, H = build.envelope.H;
  const fs = Math.max(W, H) / 21;
  const padL = fs * 5.2, padR = fs * 5.2, padT = fs * 1.2, padB = fs * 3;
  const y = (v: number) => H - v;
  // drawer boxes sit behind their fronts; doors and drawer fronts are drawn last, over the openings
  const FRONTMOST = new Set(['door', 'door-stile', 'door-rail', 'glass', 'slider', 'drawer-front']);
  const front = build.parts.filter((p) => p.role !== 'back' && !/^drawer-(side|rear|bottom)$/.test(p.role))
    .sort((a, b) => Number(FRONTMOST.has(a.role)) - Number(FRONTMOST.has(b.role)));
  const parts = front.map((p) => {
    const pl = p.placement;
    return { id: p.id, label: p.label, x: pl.x, y: y(pl.y + pl.sy), width: pl.sx, height: pl.sy };
  });
  // The back is seen inside the carcass: clip each piece to the sides, the bottom's top face and the top's underside.
  const sides = front.filter((p) => p.role === 'side');
  const xMin = sides.length ? Math.min(...sides.map((p) => p.placement.x + p.placement.sx).filter((v) => v < W / 2), W) : 0;
  const xMax = sides.length ? Math.max(...sides.map((p) => p.placement.x).filter((v) => v > W / 2), 0) : W;
  const bottom = front.find((p) => p.role === 'bottom');
  const top = front.find((p) => p.role === 'top');
  const yMin = bottom ? bottom.placement.y + bottom.placement.sy : 0;
  const yMax = top ? top.placement.y : H;
  const backs = build.parts.filter((p) => p.role === 'back').map((p) => {
    const pl = p.placement;
    const x0 = Math.max(pl.x, xMin), x1 = Math.min(pl.x + pl.sx, xMax);
    const y0 = Math.max(pl.y, yMin), y1 = Math.min(pl.y + pl.sy, yMax);
    return { id: p.id, label: p.label, x: x0, y: y(y1), width: Math.max(0, x1 - x0), height: Math.max(0, y1 - y0) };
  });
  const columns = new Set(build.openings.map((o) => o.bounds.x0));
  const single = columns.size <= 1;
  const openings = build.openings.map((o, i) => {
    const yTop = y(o.bounds.y1), yBottom = y(o.bounds.y0);
    return single
      ? { index: o.index ?? i, label: o.label, text: fmtMm(mm(o.clearMm)), x: W + fs * 0.7, tickX1: W + fs * 0.4, tickX2: W + fs, yTop, yBottom, textX: W + fs * 1.3, textY: (yTop + yBottom) / 2 }
      : { index: o.index ?? i, label: o.label, text: fmtMm(mm(o.clearMm)), x: (o.bounds.x0 + o.bounds.x1) / 2, tickX1: 0, tickX2: 0, yTop, yBottom, textX: (o.bounds.x0 + o.bounds.x1) / 2, textY: (yTop + yBottom) / 2, inside: true };
  });
  const leftDatums = build.datums.filter((d) => (d.bay ?? 0) === 0);
  const datums = leftDatums.map((d, i) => ({ shelfNo: i + 1, y: y(d.mm), x1: -fs * 0.3, x2: -fs * 1.1, textX: -fs * 1.4, text: fmtMm(mm(d.mm)) }));
  const ops = build.openings.map((o) => fmtMm(mm(o.clearMm))).join(', ');
  const dts = leftDatums.map((d) => fmtNum(d.mm)).join(', ');
  const bays = columns.size;
  return {
    viewBox: [-padL, -padT, W + padL + padR, H + padT + padB],
    W, H, fontSize: fs,
    back: backs[0] ?? null,
    backs,
    parts, openings, datums,
    width: { y: H + fs * 1.3, tickY1: H + fs * 0.8, tickY2: H + fs * 1.8, textX: W / 2, textY: H + fs * 2.4, text: fmtMm(mm(W)), floorX: -fs * 1.4, floorY: H + fs * 1.3 },
    ariaLabel: single
      ? `Front elevation: ${fmtNum(W)} wide by ${fmtNum(H)} high, ${build.openings.length} opening${build.openings.length === 1 ? '' : 's'} of ${ops} millimetres from the bottom up.` +
        (leftDatums.length ? ` Shelf undersides from the floor: ${dts} millimetres.` : '')
      : `Front elevation: ${fmtNum(W)} wide by ${fmtNum(H)} high, ${bays} bays, ${build.openings.length} openings of ${ops} millimetres, bay by bay from the bottom up.` +
        (leftDatums.length ? ` Bay 1 shelf undersides from the floor: ${dts} millimetres.` : ''),
  };
}

export interface ElevationOptions {
  /** Adds data-elev-part to the back so it is selectable (default true). */
  interactive?: boolean;
  /** Selected part id (class is-sel). */
  sel?: string | null;
}

const n = (v: number) => String(Math.round(v * 1000) / 1000);

export function elevationSvg(build: ElevationSource, { interactive = true, sel = null }: ElevationOptions = {}): string {
  const m = elevationModel(build);
  const fs = m.fontSize;
  const back = m.backs.map((bk) => `<rect class="el-back${sel === bk.id ? ' is-sel' : ''}"${interactive ? ` data-elev-part="${esc(bk.id)}"` : ''} x="${n(bk.x)}" y="${n(bk.y)}" width="${n(bk.width)}" height="${n(bk.height)}"><title>${esc(bk.label)}</title></rect>`).join('');
  const rects = m.parts.map((p) =>
    `<rect class="el-part${p.id === sel ? ' is-sel' : ''}"${interactive ? ` data-elev-part="${esc(p.id)}"` : ''} x="${n(p.x)}" y="${n(p.y)}" width="${n(p.width)}" height="${n(p.height)}"><title>${esc(p.label)}</title></rect>`).join('');
  const ops = m.openings.map((o) => o.inside
    ? `<g class="el-op el-op--in"><text x="${n(o.textX)}" y="${n(o.textY)}" text-anchor="middle" dominant-baseline="central" font-size="${n(fs * 0.8)}">${esc(o.text)}</text></g>`
    : `<g class="el-op"><line x1="${n(o.x)}" x2="${n(o.x)}" y1="${n(o.yTop)}" y2="${n(o.yBottom)}"/>` +
    `<line x1="${n(o.tickX1)}" x2="${n(o.tickX2)}" y1="${n(o.yTop)}" y2="${n(o.yTop)}"/><line x1="${n(o.tickX1)}" x2="${n(o.tickX2)}" y1="${n(o.yBottom)}" y2="${n(o.yBottom)}"/>` +
    `<text x="${n(o.textX)}" y="${n(o.textY)}" dominant-baseline="central" font-size="${n(fs)}">${esc(o.text)}</text></g>`).join('');
  const datums = m.datums.map((d) =>
    `<g class="el-datum"><line x1="${n(d.x1)}" x2="${n(d.x2)}" y1="${n(d.y)}" y2="${n(d.y)}"/><text x="${n(d.textX)}" y="${n(d.y)}" dominant-baseline="central" text-anchor="end" font-size="${n(fs * 0.92)}">${esc(d.text)}</text></g>`).join('');
  const w = m.width;
  const dim = `<g class="el-dim"><line x1="0" x2="${n(m.W)}" y1="${n(w.y)}" y2="${n(w.y)}"/><line x1="0" x2="0" y1="${n(w.tickY1)}" y2="${n(w.tickY2)}"/><line x1="${n(m.W)}" x2="${n(m.W)}" y1="${n(w.tickY1)}" y2="${n(w.tickY2)}"/>` +
    `<text x="${n(w.textX)}" y="${n(w.textY)}" text-anchor="middle" font-size="${n(fs)}">${esc(w.text)}</text>` +
    `<text x="${n(w.floorX)}" y="${n(w.floorY)}" text-anchor="end" dominant-baseline="central" font-size="${n(fs * 0.8)}" class="el-cap">floor</text></g>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" class="elev" viewBox="${m.viewBox.map(n).join(' ')}" role="img" aria-label="${esc(m.ariaLabel)}">${back}${rects}${ops}${datums}${dim}</svg>`;
}
